/**
 * Admin event detail — certificates within an event.
 *
 * Phase: 6
 *
 * Lists certificates with action column (Preview opens admin PDF endpoint in
 * new tab; Edit opens a modal to rename holder; Delete confirms then removes).
 * "Sertifika Ekle" button routes to /admin/events/:id/upload.
 */

import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconArrowLeft,
  IconCloudUpload,
  IconEdit,
  IconExternalLink,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  adminPdfUrl,
  deleteCertificate,
  getEvent,
  listCertificates,
  updateCertificate,
} from "../../api/admin";
import type { CertificateOut } from "../../api/types";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function EventDetail() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<CertificateOut | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CertificateOut | null>(null);

  const eventQuery = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => getEvent(eventId),
    enabled: !!eventId,
  });

  const certsQuery = useQuery({
    queryKey: ["certificates", eventId],
    queryFn: () => listCertificates(eventId),
    enabled: !!eventId,
  });

  const editForm = useForm({
    initialValues: { holder_name: "" },
    validate: {
      holder_name: (v) => (v.trim().length < 2 ? "İsim çok kısa." : null),
    },
  });

  function openEdit(cert: CertificateOut) {
    setEditing(cert);
    editForm.setValues({ holder_name: cert.holder_name });
  }

  const updateMutation = useMutation({
    mutationFn: async (values: { holder_name: string }) => {
      if (!editing) return;
      return updateCertificate(editing.id, { holder_name: values.holder_name.trim() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificates", eventId] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!deleteTarget) return;
      return deleteCertificate(deleteTarget.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificates", eventId] });
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setDeleteTarget(null);
    },
  });

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component={Link} to="/admin/events">
          Etkinlikler
        </Anchor>
        <Text>{eventQuery.data?.name ?? "..."}</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="end">
        <Stack gap={2}>
          <Group gap="sm" align="center">
            <Title order={2}>{eventQuery.data?.name ?? "Yükleniyor…"}</Title>
            {eventQuery.data && (
              <Badge size="lg" variant="light" style={{ fontFamily: "monospace" }}>
                {eventQuery.data.serial_number}
              </Badge>
            )}
          </Group>
          <Group gap="md">
            {eventQuery.data?.event_date && (
              <Text size="sm" c="dimmed">
                Tarih: {eventQuery.data.event_date}
              </Text>
            )}
            <Text size="sm" c="dimmed">
              Slug:{" "}
              <Text component="span" ff="monospace">
                {eventQuery.data?.slug ?? "—"}
              </Text>
            </Text>
            {eventQuery.data && (
              <Badge variant="light" color="gray">
                {eventQuery.data.certificate_count} sertifika
              </Badge>
            )}
          </Group>
        </Stack>
        <Group>
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin/events")}
          >
            Geri
          </Button>
          <Button
            leftSection={<IconCloudUpload size={16} />}
            onClick={() => navigate(`/admin/events/${eventId}/upload`)}
          >
            Sertifika Ekle
          </Button>
        </Group>
      </Group>

      <Card withBorder shadow="xs" padding={0}>
        <Box style={{ overflowX: "auto" }}>
          <Table verticalSpacing="sm" highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Sertifika Sahibi</Table.Th>
                <Table.Th>Boyut</Table.Th>
                <Table.Th>Veriliş</Table.Th>
                <Table.Th style={{ width: 140 }}>İşlemler</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {certsQuery.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      Yükleniyor…
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {certsQuery.isError && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Alert color="red">Sertifikalar yüklenemedi.</Alert>
                  </Table.Td>
                </Table.Tr>
              )}
              {certsQuery.data?.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      Henüz sertifika yok. "Sertifika Ekle" ile yükleyin.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {certsQuery.data?.map((cert) => (
                <Table.Tr key={cert.id}>
                  <Table.Td>
                    <Text fw={500}>{cert.holder_name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatBytes(cert.file_size)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{cert.issued_at ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="PDF'i önizle">
                        <ActionIcon
                          variant="subtle"
                          component="a"
                          href={adminPdfUrl(cert.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="İsmi düzenle">
                        <ActionIcon variant="subtle" onClick={() => openEdit(cert)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Sil">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleteTarget(cert)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Card>

      {/* Edit holder name */}
      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title="Sertifika Sahibini Düzenle"
        size="md"
      >
        {editing && (
          <form onSubmit={editForm.onSubmit((values) => updateMutation.mutate(values))}>
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                PDF dosyası değiştirilemez. Yanlış PDF yüklendiyse sertifikayı silip yeniden
                yükleyin.
              </Text>
              <TextInput
                label="Sertifika Sahibi"
                required
                {...editForm.getInputProps("holder_name")}
              />
              <Group justify="end">
                <Button variant="default" onClick={() => setEditing(null)}>
                  İptal
                </Button>
                <Button type="submit" loading={updateMutation.isPending}>
                  Güncelle
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>

      {/* Delete confirmation */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Sertifikayı Sil"
        size="md"
      >
        {deleteTarget && (
          <Stack gap="md">
            <Text>
              <strong>{deleteTarget.holder_name}</strong> adına olan sertifikayı silmek
              üzeresiniz. Bu işlem geri alınamaz.
            </Text>
            <Group justify="end">
              <Button variant="default" onClick={() => setDeleteTarget(null)}>
                İptal
              </Button>
              <Button
                color="red"
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                Sil
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
